import { useEffect, useMemo, useRef, useState } from 'react'
import { App } from '@capacitor/app'
import { SignInWithApple } from '@capacitor-community/apple-sign-in'
import { useClerk, useSignIn, useSignUp, useUser } from '@clerk/clerk-react'
import type { OAuthStrategy, PhoneCodeFactor } from '@clerk/types'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

// COMPONENTS
import Button from '@components/atoms/button'
import Divider from '@components/atoms/divider'
import Spinner from '@components/atoms/spinner'
import TextField from '@components/atoms/textfield'
import ReferralCard from '@components/molecules/referral-card'
import { Column } from '@components/templates/column'

// UTILITIES
import { links } from '@constants'
import { useInput } from '@hooks/inputs'
import { useFixedSettings } from '@components/templates/fixed/_localStore/_hooks'
import { platform } from '@utils/capacitor-helper'
import { getErrorMessage } from '@utils/error-helper'
import { supportedOAuthProviders } from '@utils/oauth'
import { formatInput, normalizeInput } from '@utils/phone-number-helper'

// ROUTES
import { useAuthSettings } from '../_localStore/_hooks'
import { AuthIntroScreen } from './_components/auth-intro-screen'

import './sign-in.css'

type SignInView = 'intro' | 'form'
type SignInIntent = 'sign-in' | 'sign-up' | null

interface SignInLocationState {
    initialView?: SignInView
    intent?: SignInIntent
}

const placeholder = '+1 (555) 555-5555'
const maxPhoneLength = placeholder.length
const formId = 'auth-sign-in-form'
const nativeOAuthPendingKey = 'pending-native-oauth'

function createNonce(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function getNativeUniversalLinkOrigin(): string {
    return import.meta.env.DEV
        ? 'https://dev.group.splitcard.com'
        : 'https://group.splitcard.com'
}

function parseSignInLocationState(state: unknown): Required<SignInLocationState> {
    if (typeof state !== 'object' || state == null) {
        return { initialView: 'intro', intent: null }
    }

    const locationState = state as { initialView?: unknown, intent?: unknown }

    return {
        initialView: locationState.initialView === 'form' ? 'form' : 'intro',
        intent: locationState.intent === 'sign-in' || locationState.intent === 'sign-up'
            ? locationState.intent
            : null
    }
}

function isIdentifierNotFoundError(error: unknown): boolean {
    if (typeof error !== 'object' || error == null || !('errors' in error)) return false

    const errors = (error as { errors?: Array<{ code?: string }> }).errors
    return Array.isArray(errors) && errors[0]?.code === 'form_identifier_not_found'
}

function isDevBrowserUnauthenticatedError(error: unknown): boolean {
    if (typeof error !== 'object' || error == null || !('errors' in error)) return false

    const errors = (error as { errors?: Array<{ code?: string }> }).errors
    return Array.isArray(errors) && errors[0]?.code === 'dev_browser_unauthenticated'
}

function isUserCancelledAppleSignIn(error: unknown): boolean {
    if (typeof error !== 'object' || error == null) return false

    const code = ('code' in error && typeof error.code === 'string') ? error.code.toLowerCase() : ''
    const message = ('message' in error && typeof error.message === 'string') ? error.message.toLowerCase() : ''

    return (
        code.includes('cancel')
        || code.includes('request_canceled')
        || message.includes('cancel')
    )
}

const AuthSignInScreen = () => {
    const { setIsNewUser } = useAuthSettings()
    const { createToast } = useFixedSettings()
    const clerk = useClerk()
    const { isLoaded, isSignedIn } = useUser()
    const { signIn, setActive } = useSignIn()
    const { signUp } = useSignUp()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const isWebPlatform = platform === 'web'
    const isIosPlatform = platform === 'ios'
    const socialButtonGridStyle = {
        gridTemplateColumns: `repeat(${supportedOAuthProviders.length}, minmax(0, 1fr))`
    }

    const [view, setView] = useState<SignInView>(() => parseSignInLocationState(location.state).initialView)
    const [intent, setIntent] = useState<SignInIntent>(() => parseSignInLocationState(location.state).intent)
    const [socialLoadingStrategy, setSocialLoadingStrategy] = useState<OAuthStrategy | null>(null)
    const [countryCode] = useState('+1')
    const [phoneNumber, phoneNumberProps] = useInput({
        onChange: (value) => formatInput(value, phoneNumber)
    })

    const hasReferralId = useMemo(() => (
        !!searchParams.get('referralId')
    ), [searchParams])

    useEffect(() => {
        const state = parseSignInLocationState(location.state)
        setView(state.initialView)
        setIntent(state.intent)
        setSocialLoadingStrategy(null)
    }, [location.key, location.state])

    const [isAwaitingNativeOAuth, setIsAwaitingNativeOAuth] = useState(
        () => !isWebPlatform && window.sessionStorage.getItem(nativeOAuthPendingKey) === 'true'
    )
    const nativeOAuthDidLeaveAppRef = useRef(false)

    // When a native OAuth flow is pending, poll Clerk's API to detect the
    // session created server-side. The OAuth browser opens externally (Safari),
    // so the WebView and React app stay alive while this polls.
    useEffect(() => {
        if (isWebPlatform || !isAwaitingNativeOAuth) return
        let cancelled = false

        async function pollForSession() {
            while (!cancelled) {
                await new Promise(resolve => window.setTimeout(resolve, 500))
                if (cancelled) break

                try {
                    await clerk.client?.reload()
                    const session = clerk.client?.activeSessions?.[0]
                    if (session) {
                        window.sessionStorage.removeItem(nativeOAuthPendingKey)
                        setSocialLoadingStrategy(null)
                        setIsAwaitingNativeOAuth(false)
                        await clerk.setActive({ session })
                        break
                    }
                } catch (error) {
                    console.error('[ Auth Sign In ] poll error', error)
                }
            }
        }

        pollForSession()
        return () => { cancelled = true }
    }, [isAwaitingNativeOAuth, isWebPlatform, clerk])

    useEffect(() => {
        if (isWebPlatform || !isAwaitingNativeOAuth) return

        let cancelled = false

        async function clearIfNoSession() {
            const start = Date.now()

            while (!cancelled && Date.now() - start < 2000) {
                try {
                    await clerk.client?.reload()
                    if (clerk.client?.activeSessions?.[0]) return
                } catch (error) {
                    console.error('[ Auth Sign In ] resume check error', error)
                }

                await new Promise(resolve => window.setTimeout(resolve, 250))
            }

            if (cancelled) return

            window.sessionStorage.removeItem(nativeOAuthPendingKey)
            setIsAwaitingNativeOAuth(false)
            setSocialLoadingStrategy(null)
        }

        function handleVisibilityChange() {
            if (document.visibilityState === 'hidden') {
                nativeOAuthDidLeaveAppRef.current = true
                return
            }

            if (document.visibilityState === 'visible' && nativeOAuthDidLeaveAppRef.current) {
                nativeOAuthDidLeaveAppRef.current = false
                void clearIfNoSession()
            }
        }

        function handleFocus() {
            if (!nativeOAuthDidLeaveAppRef.current) return
            nativeOAuthDidLeaveAppRef.current = false
            void clearIfNoSession()
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', handleFocus)
        const resumeListener = App.addListener('resume', () => {
            if (!nativeOAuthDidLeaveAppRef.current) return
            nativeOAuthDidLeaveAppRef.current = false
            void clearIfNoSession()
        })

        return () => {
            cancelled = true
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('focus', handleFocus)
            resumeListener
                .then(listener => listener.remove())
                .catch(error => console.error('[ App.addListener ] resume', error))
        }
    }, [isAwaitingNativeOAuth, isWebPlatform, clerk])

    function showSignInForm() {
        setIntent('sign-in')
        setView('form')
    }

    function showCreateAccountForm() {
        setIntent('sign-up')
        setView('form')
    }

    async function completeNativeAppleSignIn(identityToken: string) {
        if (signIn == null || signUp == null || setActive == null) throw new Error('Auth Services have yet to load.')

        const signInResponse = await signIn.create({
            strategy: 'oauth_token_apple',
            token: identityToken,
        } as Parameters<typeof signIn.create>[0])

        const userNeedsToBeCreated = signInResponse.firstFactorVerification?.status === 'transferable'
        if (userNeedsToBeCreated) {
            await signUp.create({ transfer: true })
            if (signUp.createdSessionId) {
                await setActive({ session: signUp.createdSessionId })
                return
            }
            throw new Error('Apple sign-up transfer did not return a session.')
        }

        if (signInResponse.createdSessionId) {
            await setActive({ session: signInResponse.createdSessionId })
            return
        }

        throw new Error(`Apple sign in did not return a session (status: ${signInResponse.status}).`)
    }

    async function handleSocialSignIn(strategy: OAuthStrategy) {
        try {
            if (!isLoaded) throw new Error('Auth Services have yet to load.')

            if (isSignedIn) {
                navigate('/account', { replace: true })
                return
            }

            setSocialLoadingStrategy(strategy)
            const callbackPath = '/sso-callback'
            const nativeOrigin = getNativeUniversalLinkOrigin()
            const redirectUrl = isWebPlatform
                ? new URL(callbackPath, window.location.origin).toString()
                : `${nativeOrigin}${callbackPath}`
            const redirectUrlComplete = isWebPlatform
                ? new URL(`/sign-in${document.location.search}`, window.location.origin).toString()
                : `${nativeOrigin}${callbackPath}${document.location.search}`
            const isSignUpIntent = intent !== 'sign-in'

            if (isWebPlatform) {
                // Web: authenticateWithRedirect works normally
                if (isSignUpIntent) {
                    if (signUp == null) throw new Error('Auth Services have yet to load.')
                    await signUp.authenticateWithRedirect({
                        strategy, redirectUrl, redirectUrlComplete, continueSignUp: true
                    })
                } else {
                    if (signIn == null) throw new Error('Auth Services have yet to load.')
                    await signIn.authenticateWithRedirect({
                        strategy, redirectUrl, redirectUrlComplete
                    })
                }
            } else {
                // Native: open OAuth in external browser to keep the WebView intact.
                // authenticateWithRedirect navigates the WebView away, which destroys
                // the React app and makes session detection impossible. Instead, we
                // create the OAuth attempt via API, then open the authorization URL
                // in Safari. The polling effect detects the session when it's created.
                window.sessionStorage.setItem(nativeOAuthPendingKey, 'true')
                setIsAwaitingNativeOAuth(true)
                nativeOAuthDidLeaveAppRef.current = false

                let externalUrl: string | undefined

                if (isSignUpIntent) {
                    if (signUp == null) throw new Error('Auth Services have yet to load.')
                    const result = await signUp.create({
                        strategy,
                        redirectUrl,
                        actionCompleteRedirectUrl: redirectUrlComplete,
                    } as Parameters<typeof signUp.create>[0])
                    externalUrl = result.verifications?.externalAccount?.externalVerificationRedirectURL?.href
                } else {
                    if (signIn == null) throw new Error('Auth Services have yet to load.')
                    const result = await signIn.create({
                        strategy,
                        redirectUrl,
                        actionCompleteRedirectUrl: redirectUrlComplete,
                    } as Parameters<typeof signIn.create>[0])
                    externalUrl = result.firstFactorVerification?.externalVerificationRedirectURL?.href
                }

                if (!externalUrl) {
                    throw new Error('Failed to get OAuth authorization URL.')
                }

                window.open(externalUrl, '_blank')
            }
        } catch (error: unknown) {
            console.error('[ Social Sign In ]', strategy, error)
            setSocialLoadingStrategy(null)
            setIsAwaitingNativeOAuth(false)
            window.sessionStorage.removeItem(nativeOAuthPendingKey)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to continue with social sign in.'),
            })
        }
    }

    async function handleMobileAppleSignIn() {
        try {
            if (!isIosPlatform) return
            if (!isLoaded || signIn == null || signUp == null || setActive == null) throw new Error('Auth Services have yet to load.')

            if (isSignedIn) {
                navigate('/account', { replace: true })
                return
            }

            setSocialLoadingStrategy('oauth_apple')
            const response = await SignInWithApple.authorize({
                clientId: import.meta.env.VITE_APPLE_SIGN_IN_CLIENT_ID,
                redirectURI: import.meta.env.VITE_APPLE_SIGN_IN_REDIRECT_URI,
                scopes: 'email name',
                state: createNonce(),
                nonce: createNonce(),
            })

            const identityToken = response.response?.identityToken
            if (!identityToken) {
                throw new Error('Apple sign in did not return an identity token.')
            }

            await completeNativeAppleSignIn(identityToken)
        } catch (error: unknown) {
            if (isUserCancelledAppleSignIn(error)) {
                setSocialLoadingStrategy(null)
                return
            }

            if (isDevBrowserUnauthenticatedError(error)) {
                setSocialLoadingStrategy(null)
                await handleSocialSignIn('oauth_apple')
                return
            }

            console.error('[ Apple Sign In ]', error)
            setSocialLoadingStrategy(null)
            createToast({
                type: 'error',
                text: getErrorMessage(error, 'Unable to continue with Apple on mobile.'),
            })
        }
    }

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()
            if (!isLoaded) throw new Error('Clerk has yet to load')

            // If already signed in, redirect instead of signing out.
            if (isSignedIn) {
                navigate('/account', { replace: true })
                return
            }

            const search = document.location.search
            navigate(`/verify${search}`)

            const signInRes = await attemptSignIn()
            const isSignInResource = typeof signInRes === 'object' && 'supportedFirstFactors' in signInRes
            const phoneNumber = isSignInResource
                ? signInRes.supportedFirstFactors?.find((factor) => factor.strategy === 'phone_code' && factor.primary) as PhoneCodeFactor | undefined
                : undefined

            if (isSignInResource && phoneNumber != null) {
                await signInRes.prepareFirstFactor({
                    strategy: 'phone_code',
                    phoneNumberId: phoneNumber.phoneNumberId
                })
                setIsNewUser(false)
            } else if (signInRes === 'sign-up') {
                const signUpRes = await attemptSignUp()
                if (signUpRes === 'success') setIsNewUser(true)
                else throw signUpRes
            } else throw signInRes
        } catch (error: unknown) {
            const state: SignInLocationState = {
                initialView: 'form',
                intent
            }

            navigate(`/sign-in${document.location.search}`, {
                replace: true,
                state,
            })

            const defaultMsg = 'An issue occured while Authenticating your account.'
            createToast({
                type: 'error',
                text: getErrorMessage(error, defaultMsg),
            })
        }
    }

    async function attemptSignIn() {
        try {
            if (signIn == null) return { event: 'error', error: { errors: [{ longMessage: 'Auth Services have yet to load.' }] } }
            const result = await signIn.create({ identifier: `${countryCode}${normalizeInput(phoneNumber)}` })

            if (result.status === 'complete' || result.status === 'needs_first_factor') return result
            else return { event: 'error', error: { errors: [{ longMessage: `Failed with status: ${result.status}` }] } }
        } catch (error: unknown) {
            if (isIdentifierNotFoundError(error)) return 'sign-up'
            else return { event: 'error', error }
        }
    }

    async function attemptSignUp() {
        try {
            if (signUp == null) return { event: 'error', error: { errors: [{ longMessage: 'Auth Services have yet to load.' }] } }

            await signUp.create({ phoneNumber: `${countryCode}${normalizeInput(phoneNumber)}` })
            await signUp.preparePhoneNumberVerification({ strategy: 'phone_code' })
            return 'success'
        } catch (error: unknown) {
            return { event: 'error', error }
        }
    }

    const isSubmitable = useMemo(() => validateInputs(phoneNumberProps.value), [phoneNumberProps.value])
    const headerText = intent === 'sign-in'
        ? 'Sign In'
        : intent === 'sign-up'
            ? 'Create Account'
            : 'Sign In or Create Account'
    const helperText = intent === 'sign-up'
        ? 'Enter your phone number to get started.'
        : 'Enter your phone number to continue.'

    const nativeOAuthDialogRef = useRef<HTMLDialogElement>(null)

    useEffect(() => {
        if (!nativeOAuthDialogRef.current) return
        if (isAwaitingNativeOAuth) nativeOAuthDialogRef.current.showModal()
        else nativeOAuthDialogRef.current.close()
    }, [isAwaitingNativeOAuth])

    if (view === 'intro') {
        return (
            <>
                <Column.Content className='auth-sign-in-intro-host' padding='none'>
                    <AuthIntroScreen onCreateAccount={showCreateAccountForm} onSignIn={showSignInForm} />
                </Column.Content>
                <Column.Footer hidden />
            </>
        )
    }

    return (
        <>
            <Column.Content className='auth-sign-in-screen'>
                <img className='splitcard-logo' src='/splitcard/logo/white.png' />
                <div className='flex flex-col justify-center gap-4 w-full'>
                    <h1 style={{ lineHeight: 1.15, marginBottom: '0.25rem' }}>{headerText}</h1>
                    <p className='auth-sign-in-intent-helper'>{helperText}</p>
                    <div className='flex flex-col'>
                        <form id={formId} onSubmit={handleSubmit}>
                            <label htmlFor='phone-number' />
                            <TextField
                                {...phoneNumberProps}
                                type='tel'
                                autoComplete='tel'
                                autoCapitalize='off'
                                autoCorrect='off'
                                inputMode='tel'
                                id='phone-number'
                                name='phone-number'
                                aria-labelledby='phone-number'
                                maxLength={maxPhoneLength}
                                placeholder='Phone number'
                                divideElementLeft={true}
                                inlineElementLeft={<p><strong>+1</strong></p>}
                                required
                            />
                        </form>
                    </div>
                    {isWebPlatform && (
                        <div className='auth-sign-in-social'>
                            <Divider className='auth-sign-in-social-divider'>{intent === 'sign-up' ? 'or sign up with' : 'or continue with'}</Divider>
                            <div className='auth-sign-in-social-buttons' style={socialButtonGridStyle}>
                                {supportedOAuthProviders.map(({ label, strategy, iconSrc, className }) => (
                                    <Button
                                        key={strategy}
                                        theme='secondary'
                                        className={`auth-sign-in-social-button ${className || ''}`}
                                        onClick={() => handleSocialSignIn(strategy)}
                                        disabled={socialLoadingStrategy != null}
                                        aria-label={label}
                                        title={label}
                                    >
                                        {iconSrc && (socialLoadingStrategy !== strategy) && (
                                            <img
                                                className='auth-sign-in-social-button-icon'
                                                src={iconSrc}
                                                alt=''
                                                aria-hidden='true'
                                            />
                                        )}
                                        {socialLoadingStrategy === strategy
                                            ? <Spinner className='auth-sign-in-social-loading-indicator' indicator='circle' role='status' aria-label={`Connecting to ${label}`} />
                                            : null
                                        }
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}
                    {!isWebPlatform && isIosPlatform && (
                        <div className='auth-sign-in-social'>
                            <Divider className='auth-sign-in-social-divider'>{intent === 'sign-up' ? 'or sign up with' : 'or continue with'}</Divider>
                            <div className='auth-sign-in-social-buttons' style={socialButtonGridStyle}>
                                {supportedOAuthProviders.map(({ label, strategy, iconSrc, className }) => (
                                    <Button
                                        key={strategy}
                                        theme='secondary'
                                        className={`auth-sign-in-social-button ${className || ''}`}
                                        onClick={strategy === 'oauth_apple' ? handleMobileAppleSignIn : () => handleSocialSignIn(strategy)}
                                        disabled={socialLoadingStrategy != null}
                                        aria-label={label}
                                        title={label}
                                    >
                                        {iconSrc && (socialLoadingStrategy !== strategy) && (
                                            <img
                                                className='auth-sign-in-social-button-icon'
                                                src={iconSrc}
                                                alt=''
                                                aria-hidden='true'
                                            />
                                        )}
                                        {socialLoadingStrategy === strategy
                                            ? <Spinner className='auth-sign-in-social-loading-indicator' indicator='circle' role='status' aria-label={`Connecting to ${label}`} />
                                            : null
                                        }
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}
                    <small style={{ marginTop: '1rem' }}>
                        <a onClick={() => window.open(links.contactUs, '_blank')}>Need Help?</a>
                    </small>
                </div>
                {hasReferralId && (
                    <ReferralCard
                        variant='signUpClaim'
                        className='auth-sign-in-referral-card'
                    />
                )}
                <small>
                    Message and data rates may apply. By continuing, you agree to
                    our <a onClick={() => window.open(links.termsOfUse, '_blank')}>Terms of Service</a> & <a onClick={() => window.open(links.privacyPolicy, '_blank')}>Privacy Policy</a>
                </small>
                <div id='clerk-captcha'></div>
            </Column.Content>
            <Column.Footer>
                <Button theme='secondary' form={formId} style={{ width: '100%' }} type='submit' disabled={!isSubmitable || socialLoadingStrategy != null}>Continue</Button>
            </Column.Footer>
            <dialog ref={nativeOAuthDialogRef} className='auth-sign-in-oauth-dialog'>
                <div>
                    <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <Spinner />
                    </div>
                </div>
            </dialog>
        </>
    )
}

const validateInputs = (phone: string): boolean => (
    phone.length === placeholder.replace('+1 ', '').length
)

const SignInRoute: RouteObject = {
    path: 'sign-in',
    element: <AuthSignInScreen />,
}

export default SignInRoute
